import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 300;
const MARGIN = { top: 20, right: 30, bottom: 50, left: 55 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function getAddressColor(addr, thresholds) {
  const latestZ = addr.zScores[addr.zScores.length - 1];
  const absZ = Math.abs(latestZ);
  if (absZ >= thresholds.critical) return '#dc2626';
  if (absZ >= thresholds.warning) return '#f59e0b';
  return '#d1d5db';
}

function getAddressOpacity(addr, thresholds) {
  const latestZ = addr.zScores[addr.zScores.length - 1];
  const absZ = Math.abs(latestZ);
  if (absZ >= thresholds.warning) return 1;
  return 0.3;
}

export default function ZScoreTimeline({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [showOnlyFlagged, setShowOnlyFlagged] = useState(false);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0 || !data.addresses || data.addresses.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const thresholds = data.thresholds || { warning: 1.5, critical: 2.0 };

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('viewBox', `0 0 ${width} ${CHART_HEIGHT}`)
      .attr('width', '100%')
      .attr('height', CHART_HEIGHT)
      .style('overflow', 'visible');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const n = data.dates.length;
    const parsedDates = data.dates.map((d) => new Date(d));

    // Filter addresses based on toggle
    const visibleAddresses = showOnlyFlagged
      ? data.addresses.filter((addr) => {
          const latestZ = addr.zScores[addr.zScores.length - 1];
          return Math.abs(latestZ) >= thresholds.warning;
        })
      : data.addresses;

    // X scale
    const x = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    // Y scale — gather all z-scores for domain
    const allZ = [];
    visibleAddresses.forEach((addr) => {
      addr.zScores.forEach((z) => {
        if (z != null) allZ.push(z);
      });
    });

    const zMin = allZ.length > 0 ? d3.min(allZ) : -3;
    const zMax = allZ.length > 0 ? d3.max(allZ) : 3;
    const absMax = Math.max(Math.abs(zMin), Math.abs(zMax), thresholds.critical + 0.5);
    const yDomain = [-absMax, absMax];

    const y = d3.scaleLinear()
      .domain(yDomain)
      .nice()
      .range([innerH, 0]);

    // -- Shaded bands --
    // Critical zone (|z| > 2): light red
    const critTop = y(yDomain[1]);
    const critUpperStart = y(thresholds.critical);
    const critLowerEnd = y(-thresholds.critical);
    const critBottom = y(yDomain[0]);

    // Upper critical band
    g.append('rect')
      .attr('x', 0)
      .attr('y', critTop)
      .attr('width', innerW)
      .attr('height', Math.max(0, critUpperStart - critTop))
      .attr('fill', '#dc2626')
      .attr('fill-opacity', 0.05);

    // Lower critical band
    g.append('rect')
      .attr('x', 0)
      .attr('y', critLowerEnd)
      .attr('width', innerW)
      .attr('height', Math.max(0, critBottom - critLowerEnd))
      .attr('fill', '#dc2626')
      .attr('fill-opacity', 0.05);

    // Warning zone (1.5 to 2): light amber
    const warnUpperStart = y(thresholds.warning);
    const warnLowerEnd = y(-thresholds.warning);

    // Upper warning band (between warning and critical)
    g.append('rect')
      .attr('x', 0)
      .attr('y', critUpperStart)
      .attr('width', innerW)
      .attr('height', Math.max(0, warnUpperStart - critUpperStart))
      .attr('fill', '#f59e0b')
      .attr('fill-opacity', 0.05);

    // Lower warning band (between -critical and -warning)
    g.append('rect')
      .attr('x', 0)
      .attr('y', warnLowerEnd)
      .attr('width', innerW)
      .attr('height', Math.max(0, critLowerEnd - warnLowerEnd))
      .attr('fill', '#f59e0b')
      .attr('fill-opacity', 0.05);

    // -- Grid lines --
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // -- Horizontal dashed reference lines --
    const refLines = [
      { value: 0, color: '#9ca3af' },
      { value: thresholds.warning, color: '#f59e0b' },
      { value: -thresholds.warning, color: '#f59e0b' },
      { value: thresholds.critical, color: '#dc2626' },
      { value: -thresholds.critical, color: '#dc2626' },
    ];

    refLines.forEach((ref) => {
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', y(ref.value))
        .attr('y2', y(ref.value))
        .attr('stroke', ref.color)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '6,4')
        .attr('stroke-opacity', 0.6);
    });

    // Reference line labels
    g.append('text')
      .attr('x', innerW + 4)
      .attr('y', y(thresholds.critical))
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('fill', '#dc2626')
      .text(`+${thresholds.critical}`);

    g.append('text')
      .attr('x', innerW + 4)
      .attr('y', y(-thresholds.critical))
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('fill', '#dc2626')
      .text(`-${thresholds.critical}`);

    g.append('text')
      .attr('x', innerW + 4)
      .attr('y', y(thresholds.warning))
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('fill', '#f59e0b')
      .text(`+${thresholds.warning}`);

    g.append('text')
      .attr('x', innerW + 4)
      .attr('y', y(-thresholds.warning))
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('fill', '#f59e0b')
      .text(`-${thresholds.warning}`);

    g.append('text')
      .attr('x', innerW + 4)
      .attr('y', y(0))
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text('0');

    // -- Line generator --
    const lineGen = d3.line()
      .x((d, i) => x(parsedDates[i]))
      .y((d) => y(d))
      .defined((d) => d != null)
      .curve(d3.curveMonotoneX);

    // -- Draw address lines --
    // Draw normal lines first, then warning, then critical (so outliers render on top)
    const sortedAddresses = [...visibleAddresses].sort((a, b) => {
      const aZ = Math.abs(a.zScores[a.zScores.length - 1]);
      const bZ = Math.abs(b.zScores[b.zScores.length - 1]);
      return aZ - bZ;
    });

    sortedAddresses.forEach((addr) => {
      const color = getAddressColor(addr, thresholds);
      const opacity = getAddressOpacity(addr, thresholds);
      const strokeWidth = opacity === 1 ? 1.5 : 1;

      g.append('path')
        .datum(addr.zScores)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', strokeWidth)
        .attr('stroke-opacity', opacity);
    });

    // -- Axes --

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(n, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => {
            const sign = v > 0 ? '+' : '';
            return `${sign}${v.toFixed(1)}`;
          })
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .text('Z-Score');

    // -- Crosshair + tooltip overlay --
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Highlight dot for nearest address
    const highlightDot = g.append('circle')
      .attr('r', 4)
      .attr('fill', '#fff')
      .attr('stroke-width', 2)
      .style('display', 'none');

    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx, my] = d3.pointer(event);
        const bisect = d3.bisector((d) => d).left;
        const dateAtMouse = x.invert(mx);
        const timestamps = parsedDates.map((d) => d.getTime());
        let idx = bisect(timestamps, dateAtMouse.getTime());
        if (idx > 0 && idx < n) {
          const d0 = timestamps[idx - 1];
          const d1 = timestamps[idx];
          idx = (dateAtMouse.getTime() - d0) > (d1 - dateAtMouse.getTime()) ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(n - 1, idx));

        const cx = x(parsedDates[idx]);
        crosshair
          .attr('x1', cx)
          .attr('x2', cx)
          .style('display', null);

        // Find nearest address by y distance at this date index
        const mouseY = y.invert(my);
        let nearest = null;
        let nearestDist = Infinity;

        visibleAddresses.forEach((addr) => {
          const zVal = addr.zScores[idx];
          if (zVal == null) return;
          const dist = Math.abs(zVal - mouseY);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = addr;
          }
        });

        if (nearest) {
          const nearestZ = nearest.zScores[idx];
          const dotColor = getAddressColor(nearest, thresholds);
          highlightDot
            .attr('cx', cx)
            .attr('cy', y(nearestZ))
            .attr('stroke', dotColor)
            .style('display', null);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: data.dates[idx],
            label: nearest.label,
            state: nearest.state,
            zScore: nearestZ,
            isOutlier: nearest.isOutlier,
            color: dotColor,
          });
        }
      })
      .on('mouseleave', () => {
        crosshair.style('display', 'none');
        highlightDot.style('display', 'none');
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, showOnlyFlagged]);

  // Handle window resize
  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        setTooltip(null);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [data]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0 || !data.addresses || data.addresses.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Outlier Tracking</div>
          <div style={styles.subtitle}>
            Z-score evolution over time — addresses drifting from the mean
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No z-score data available
        </div>
      </div>
    );
  }

  const thresholds = data.thresholds || { warning: 1.5, critical: 2.0 };

  // Count flagged addresses for toggle label
  const warningCount = data.addresses.filter((addr) => {
    const latestZ = Math.abs(addr.zScores[addr.zScores.length - 1]);
    return latestZ >= thresholds.warning && latestZ < thresholds.critical;
  }).length;
  const criticalCount = data.addresses.filter((addr) => {
    const latestZ = Math.abs(addr.zScores[addr.zScores.length - 1]);
    return latestZ >= thresholds.critical;
  }).length;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Outlier Tracking</div>
        <div style={styles.subtitle}>
          Z-score evolution over time — addresses drifting from the mean
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <label style={styles.toggleLabel} onClick={() => setShowOnlyFlagged(!showOnlyFlagged)}>
          <input
            type="checkbox"
            checked={showOnlyFlagged}
            onChange={() => setShowOnlyFlagged(!showOnlyFlagged)}
            style={{ cursor: 'pointer' }}
          />
          <span>Show only flagged addresses</span>
        </label>
        <span style={{ color: '#9ca3af', fontSize: '11px' }}>
          {criticalCount} critical, {warningCount} warning of {data.addresses.length} total
        </span>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#d1d5db" strokeWidth="1.5" strokeOpacity="0.3" />
          </svg>
          <span>Normal (|z| &lt; {thresholds.warning})</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#f59e0b" strokeWidth="1.5" />
          </svg>
          <span>Warning (|z| &ge; {thresholds.warning})</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#dc2626" strokeWidth="1.5" />
          </svg>
          <span>Critical (|z| &ge; {thresholds.critical})</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#9ca3af" strokeWidth="1" strokeDasharray="6,4" />
          </svg>
          <span>Thresholds</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Address</span>
            <span style={{ fontWeight: 600, color: tooltip.color }}>{tooltip.label}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>State</span>
            <span style={{ fontWeight: 600 }}>{tooltip.state}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Z-Score</span>
            <span
              style={{
                fontWeight: 600,
                color: Math.abs(tooltip.zScore) >= thresholds.critical
                  ? '#dc2626'
                  : Math.abs(tooltip.zScore) >= thresholds.warning
                  ? '#f59e0b'
                  : '#1f2937',
              }}
            >
              {tooltip.zScore >= 0 ? '+' : ''}{tooltip.zScore.toFixed(2)}
            </span>
          </div>
          {tooltip.isOutlier && (
            <div style={{
              marginTop: '4px',
              padding: '2px 6px',
              background: '#fee2e2',
              color: '#991b1b',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              textAlign: 'center',
            }}>
              OUTLIER
            </div>
          )}
        </div>
      )}
    </div>
  );
}
